/**
 * Sidebar navigation component (D-02, D-10, UI-SPEC Sidebar contract).
 *
 * - 240px expanded (w-60), 64px collapsed (w-16) icon rail
 * - Fixed position, full height, surface background, border-soft right border
 * - Logo mark + "Ads Command Center" text at top (hidden when collapsed)
 * - Tenant switcher (D-10): clears queryClient + navigates to /dashboard
 * - 8 route items with Phosphor icons and active state indicator
 * - Collapse toggle at bottom with Motion animation (200ms ease-out)
 * - Badge pills slot for failure counts (Comandos, Eventos, Vendas)
 */
import { useLocation, useNavigate } from 'react-router';
import { motion } from 'motion/react';
import {
  ChartBar,
  Buildings,
  Plug,
  Lightning,
  Stack,
  ShoppingCart,
  ClockCounterClockwise,
  Gear,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useSidebarStore } from '../../stores/sidebar.store';
import { useTenantStore } from '../../stores/tenant.store';
import { queryClient } from '../../main';

export interface BadgeCounts {
  commands?: number;
  events?: number;
  sales?: number;
}

interface NavItem {
  label: string;
  path: string;
  icon: Icon;
  badgeKey?: keyof BadgeCounts;
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: ChartBar },
  { label: 'Contas', path: '/accounts', icon: Buildings },
  { label: 'Integracoes', path: '/integrations', icon: Plug },
  {
    label: 'Comandos',
    path: '/commands',
    icon: Lightning,
    badgeKey: 'commands',
  },
  {
    label: 'Eventos/Jobs',
    path: '/events',
    icon: Stack,
    badgeKey: 'events',
  },
  {
    label: 'Vendas',
    path: '/sales',
    icon: ShoppingCart,
    badgeKey: 'sales',
  },
  { label: 'Auditoria', path: '/audit', icon: ClockCounterClockwise },
];

const settingsItem: NavItem = {
  label: 'Configuracoes',
  path: '/settings',
  icon: Gear,
};

export function Sidebar({ badgeCounts }: { badgeCounts?: BadgeCounts }) {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const location = useLocation();
  const navigate = useNavigate();
  const organizationName = useTenantStore((s) => s.organizationName);

  const handleTenantSwitch = () => {
    // D-10: tenant switcher clears all queries and navigates to dashboard.
    // For MVP single-tenant, this acts as a "reset view" action.
    // When multi-org is supported, this will open a tenant selector first.
    queryClient.clear();
    navigate('/dashboard');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <motion.aside
      className="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border-soft bg-surface"
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {/* Logo */}
      <div className="flex h-14 items-center gap-3 border-b border-border-soft px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-navy">
          <span className="text-[12px] font-semibold text-white">AC</span>
        </div>
        {!collapsed && (
          <motion.span
            className="truncate text-[14px] font-semibold text-text-strong"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.05, duration: 0.15 }}
          >
            Ads Command Center
          </motion.span>
        )}
      </div>

      {/* Tenant switcher (D-10) */}
      <button
        type="button"
        onClick={handleTenantSwitch}
        className="mx-2 mt-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-text-muted transition-colors hover:bg-surface-muted"
        title={
          organizationName
            ? `Organizacao: ${organizationName}`
            : 'Sem organizacao'
        }
      >
        <Buildings size={16} className="shrink-0" />
        {!collapsed && (
          <span className="truncate">
            {organizationName ?? 'Sem organizacao'}
          </span>
        )}
      </button>

      {/* Main navigation */}
      <nav className="mt-2 flex flex-1 flex-col gap-0.5 px-2">
        {mainNavItems.map((item) => {
          const active = isActive(item.path);
          const IconComponent = item.icon;
          const badgeCount = item.badgeKey
            ? badgeCounts?.[item.badgeKey]
            : undefined;

          return (
            <button
              key={item.path}
              type="button"
              onClick={() => navigate(item.path)}
              className={`group relative flex items-center gap-3 rounded-md px-3 py-2 text-[14px] transition-colors ${
                active
                  ? 'bg-surface-muted font-semibold text-text-strong'
                  : 'text-text-muted hover:bg-surface-muted hover:text-text-strong'
              }`}
            >
              {/* Active indicator bar */}
              {active && (
                <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-accent" />
              )}

              <IconComponent
                size={20}
                weight={active ? 'bold' : 'regular'}
                className="shrink-0"
              />

              {!collapsed && (
                <span className="truncate">{item.label}</span>
              )}

              {/* Badge pill */}
              {badgeCount != null && badgeCount > 0 && (
                <span
                  className={`flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[12px] font-semibold text-white ${
                    collapsed
                      ? 'absolute -right-1 -top-1'
                      : 'ml-auto'
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Divider + settings + collapse toggle */}
      <div className="mt-auto border-t border-border-soft px-2 pb-2 pt-2">
        {/* Settings item */}
        <button
          type="button"
          onClick={() => navigate(settingsItem.path)}
          className={`group flex w-full items-center gap-3 rounded-md px-3 py-2 text-[14px] transition-colors ${
            isActive(settingsItem.path)
              ? 'bg-surface-muted font-semibold text-text-strong'
              : 'text-text-muted hover:bg-surface-muted hover:text-text-strong'
          }`}
        >
          {isActive(settingsItem.path) && (
            <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-accent" />
          )}
          <Gear
            size={20}
            weight={isActive(settingsItem.path) ? 'bold' : 'regular'}
            className="shrink-0"
          />
          {!collapsed && <span className="truncate">{settingsItem.label}</span>}
        </button>

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
          className="mt-1 flex w-full items-center justify-center rounded-md py-2 text-text-muted transition-colors hover:bg-surface-muted hover:text-text-strong"
        >
          {collapsed ? <CaretRight size={20} /> : <CaretLeft size={20} />}
        </button>
      </div>
    </motion.aside>
  );
}
